import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldCheck, Store, Users, Coffee, Receipt, Calendar as CalIcon, Lock, Unlock,
    Gift, KeyRound, LogOut, Search, AlertTriangle, X, Sparkles, ChevronDown, ChevronUp,
    FileText, CheckCircle2, Crown, Ban, Infinity as InfinityIcon, Euro,
} from "lucide-react";

const TOKEN_KEY = "superadmin_token";

// ---------- LOGIN ----------
const SuperAdminLogin = ({ onSuccess }) => {
    const [username, setUsername] = useState("admin");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/superadmin/login", { username, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            onSuccess(data);
        } catch (err) {
            setError(err?.response?.data?.detail || "Credenziali non valide");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-deep">
            <div className="bg-surface border-2 border-red-700/50 rounded-lg p-8 max-w-md w-full shadow-2xl" data-testid="superadmin-login-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-red-900/30 border border-red-500 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-red-300" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-red-300">Restricted Area</div>
                        <h2 className="font-display text-2xl font-bold text-warm">Super Admin</h2>
                    </div>
                </div>
                <p className="text-elegant text-xs mb-6">
                    Pannello di controllo della piattaforma. Accesso riservato al proprietario.
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="superadmin-login-form">
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-elegant mb-2">Username</label>
                        <input value={username} onChange={(e) => setUsername(e.target.value)}
                            data-testid="sa-username"
                            autoComplete="username"
                            className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md text-warm focus:border-red-500 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-elegant mb-2">Password</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="sa-password"
                            autoComplete="current-password"
                            className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md text-warm focus:border-red-500 focus:outline-none" />
                    </div>
                    {error && <div className="text-sm text-red-400" data-testid="sa-error">{error}</div>}
                    <button type="submit" disabled={loading} data-testid="sa-submit"
                        className="w-full h-12 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-bold uppercase tracking-[0.15em] rounded-md">
                        {loading ? "Accesso..." : "Accedi"}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ---------- DASHBOARD ----------
const SuperAdmin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [authed, setAuthed] = useState(false);
    const [restaurants, setRestaurants] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [actionFor, setActionFor] = useState(null);          // {id, name} → blocca/sblocca conferma
    const [giftFor, setGiftFor] = useState(null);              // {id, name} → modale regalo
    const [premiumFor, setPremiumFor] = useState(null);        // {r} → modale Attiva/Disattiva premium
    const [billingFor, setBillingFor] = useState(null);        // {id, name, billing_info} → modale dati fattur.
    const [giftDays, setGiftDays] = useState(7);
    const [giftAi, setGiftAi] = useState(50);
    const [showPwd, setShowPwd] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");
    const navigate = useNavigate();

    // attach auth header for all requests in this page
    useEffect(() => {
        if (!token) {
            setAuthed(false);
            setLoading(false);
            return;
        }
        api.get("/superadmin/me", { headers: { Authorization: `Bearer ${token}` } })
            .then(() => setAuthed(true))
            .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); setAuthed(false); })
            .finally(() => setLoading(false));
    }, [token]);

    const load = async () => {
        try {
            const { data } = await api.get("/superadmin/restaurants", { headers: { Authorization: `Bearer ${token}` } });
            setRestaurants(data);
        } catch {
            toast.error("Errore caricamento ristoranti");
        }
    };
    useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = !q ? restaurants : restaurants.filter((r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.owner_name || "").toLowerCase().includes(q) ||
            (r.vat || "").toLowerCase().includes(q) ||
            (r.email || "").toLowerCase().includes(q)
        );
        const dir = sortDir === "asc" ? 1 : -1;
        list = [...list].sort((a, b) => {
            const va = a[sortBy] ?? "";
            const vb = b[sortBy] ?? "";
            if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
            return String(va).localeCompare(String(vb)) * dir;
        });
        return list;
    }, [restaurants, search, sortBy, sortDir]);

    const toggleSort = (key) => {
        if (sortBy === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
        else { setSortBy(key); setSortDir("desc"); }
    };

    const blockToggle = async (r, reason = "") => {
        try {
            await api.patch(`/superadmin/restaurants/${r.id}/block`,
                { active: !r.active, reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(r.active ? "Ristorante bloccato" : "Ristorante riattivato");
            setActionFor(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const gift = async () => {
        try {
            const payload = {};
            if (giftDays > 0) payload.days = parseInt(giftDays, 10);
            if (giftAi > 0) payload.ai_credits = parseInt(giftAi, 10);
            const { data } = await api.patch(`/superadmin/restaurants/${giftFor.id}/gift`, payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Regalo applicato: +${data.days_added}gg, +${data.ai_credits_added} crediti AI`);
            setGiftFor(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const grantPremium = async (months, perpetual) => {
        try {
            const payload = perpetual ? { perpetual: true } : { months: parseInt(months, 10) };
            const { data } = await api.patch(
                `/superadmin/restaurants/${premiumFor.id}/grant-premium`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(
                data.perpetual
                    ? `Premium attivato a vita per ${premiumFor.name}`
                    : `Premium attivato ${data.months} mese${data.months > 1 ? "i" : ""} per ${premiumFor.name}`
            );
            setPremiumFor(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const revokePremium = async () => {
        try {
            const { data } = await api.patch(
                `/superadmin/restaurants/${premiumFor.id}/revoke-premium`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(
                data.trial_still_valid
                    ? "Premium revocato — trial residuo ancora valido"
                    : "Premium revocato — abbonamento scaduto"
            );
            setPremiumFor(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setAuthed(false);
        navigate("/login");
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-elegant">Caricamento...</div>;
    if (!authed) return <SuperAdminLogin onSuccess={(d) => { setToken(d.token); setAuthed(true); }} />;

    const SortBtn = ({ k, children }) => (
        <button onClick={() => toggleSort(k)} className="flex items-center gap-1 hover:text-brand">
            {children}
            {sortBy === k && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
        </button>
    );

    return (
        <div className="min-h-screen bg-deep text-warm p-6 lg:p-10" data-testid="superadmin-dashboard">
            {/* header */}
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-red-400 mb-1 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" /> Super Admin
                    </div>
                    <h1 className="font-display text-3xl font-bold">Pannello Piattaforma</h1>
                    <p className="text-elegant text-sm mt-1">{restaurants.length} ristoranti registrati</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setShowPricing(true)} data-testid="sa-open-pricing"
                        className="h-10 px-4 border border-emerald-700/50 hover:border-emerald-500 text-emerald-300 hover:text-emerald-200 rounded-md text-xs uppercase tracking-wider flex items-center gap-2">
                        <Euro className="w-4 h-4" /> Prezzi
                    </button>
                    <button onClick={() => setShowPwd(true)} data-testid="sa-open-pwd"
                        className="h-10 px-4 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs uppercase tracking-wider flex items-center gap-2">
                        <KeyRound className="w-4 h-4" /> Cambia credenziali
                    </button>
                    <button onClick={logout} data-testid="sa-logout"
                        className="h-10 px-4 border border-red-700/50 bg-red-900/20 hover:bg-red-900/40 text-red-300 rounded-md text-xs uppercase tracking-wider flex items-center gap-2">
                        <LogOut className="w-4 h-4" /> Esci
                    </button>
                </div>
            </div>

            {/* search */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-elegant" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        data-testid="sa-search"
                        placeholder="Cerca per nome locale, intestatario, P.IVA o email..."
                        className="w-full h-11 pl-10 pr-4 bg-deep border border-[#1f2c44] rounded-md text-warm placeholder-elegant focus:border-brand focus:outline-none" />
                </div>
            </div>

            {/* table */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-deep border-b border-[#1f2c44]">
                        <tr className="text-[10px] uppercase tracking-wider text-elegant">
                            <th className="text-left p-3"><SortBtn k="name">Ristorante</SortBtn></th>
                            <th className="text-left p-3">Intestatario</th>
                            <th className="text-left p-3">P.IVA</th>
                            <th className="text-left p-3">Email</th>
                            <th className="text-right p-3"><SortBtn k="users_count"><Users className="w-3 h-3 inline" /></SortBtn></th>
                            <th className="text-right p-3"><SortBtn k="tables_count"><Coffee className="w-3 h-3 inline" /></SortBtn></th>
                            <th className="text-right p-3"><SortBtn k="orders_count"><Receipt className="w-3 h-3 inline" /></SortBtn></th>
                            <th className="text-right p-3"><SortBtn k="days_remaining">gg trial</SortBtn></th>
                            <th className="text-right p-3">AI</th>
                            <th className="text-center p-3">Fattur.</th>
                            <th className="text-center p-3">Stato</th>
                            <th className="text-center p-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan="12" className="p-10 text-center text-elegant" data-testid="sa-no-results">
                                Nessun ristorante trovato.
                            </td></tr>
                        )}
                        {filtered.map((r) => (
                            <tr key={r.id} className="border-b border-[#1f2c44]/50 hover:bg-deep/50" data-testid={`sa-row-${r.id}`}>
                                <td className="p-3">
                                    <div className="font-bold text-warm">{r.name}</div>
                                    <div className="text-[10px] text-elegant">{r.id.slice(0, 8)}…</div>
                                </td>
                                <td className="p-3 text-elegant">{r.owner_name || "—"}</td>
                                <td className="p-3 text-elegant font-mono text-xs">{r.vat || "—"}</td>
                                <td className="p-3 text-elegant text-xs">{r.email || "—"}</td>
                                <td className="p-3 text-right font-bold">{r.users_count}</td>
                                <td className="p-3 text-right font-bold">{r.tables_count}</td>
                                <td className="p-3 text-right font-bold">{r.orders_count}</td>
                                <td className="p-3 text-right">
                                    <span className={`font-bold ${r.days_remaining <= 0 ? "text-red-400" : r.days_remaining <= 3 ? "text-amber-300" : "text-brand"}`}>
                                        {r.days_remaining}
                                    </span>
                                </td>
                                <td className="p-3 text-right text-purple-300 font-bold">{r.ai_purchased_remaining}</td>
                                <td className="p-3 text-center">
                                    <button
                                        onClick={() => setBillingFor(r)}
                                        data-testid={`sa-billing-${r.id}`}
                                        title={r.billing_complete ? "Dati fatturazione completi — clicca per vedere" : "Dati fatturazione MANCANTI"}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${
                                            r.billing_complete
                                                ? "bg-green-900/30 text-green-300 border-green-500/40 hover:bg-green-900/60"
                                                : "bg-amber-900/30 text-amber-300 border-amber-500/40 hover:bg-amber-900/60 animate-pulse"
                                        }`}
                                    >
                                        {r.billing_complete ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                        {r.billing_complete ? "OK" : "KO"}
                                    </button>
                                </td>
                                <td className="p-3 text-center">
                                    <div className="flex flex-col items-center gap-1">
                                        {r.active !== false
                                            ? <span className="text-[10px] font-bold uppercase tracking-wider bg-green-900/40 text-green-300 px-2 py-1 rounded">Attivo</span>
                                            : <span className="text-[10px] font-bold uppercase tracking-wider bg-red-900/40 text-red-300 px-2 py-1 rounded animate-pulse">Bloccato</span>
                                        }
                                        {r.premium_complimentary && (
                                            <span
                                                data-testid={`sa-premium-badge-${r.id}`}
                                                title={r.premium_perpetual ? "Premium Omaggio a vita" : (r.premium_expires_at ? `Scade il ${new Date(r.premium_expires_at).toLocaleDateString("it-IT")}` : "Premium Omaggio")}
                                                className="text-[9px] font-bold uppercase tracking-wider bg-purple-900/40 text-purple-200 border border-purple-500/50 px-2 py-0.5 rounded flex items-center gap-1"
                                            >
                                                <Crown className="w-2.5 h-2.5" />
                                                {r.premium_perpetual ? "Omaggio ∞" : "Omaggio"}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="p-3">
                                    <div className="flex justify-center gap-1">
                                        <button onClick={() => setPremiumFor(r)}
                                            data-testid={`sa-premium-${r.id}`}
                                            title={r.premium_complimentary ? "Gestisci Premium omaggio" : "Attiva Premium gratuito"}
                                            className={`w-8 h-8 rounded border flex items-center justify-center ${
                                                r.premium_complimentary
                                                    ? "border-purple-400 bg-purple-700/40 hover:bg-purple-700/70 text-purple-100"
                                                    : "border-amber-500 bg-amber-900/20 hover:bg-amber-900/50 text-amber-200"
                                            }`}>
                                            <Crown className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => { setGiftFor(r); setGiftDays(7); setGiftAi(50); }}
                                            data-testid={`sa-gift-${r.id}`}
                                            title="Regala giorni di trial o crediti AI"
                                            className="w-8 h-8 rounded border border-purple-500 bg-purple-900/20 hover:bg-purple-900/50 text-purple-200 flex items-center justify-center">
                                            <Gift className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setActionFor(r)}
                                            data-testid={`sa-block-${r.id}`}
                                            title={r.active !== false ? "Blocca ristorante" : "Sblocca ristorante"}
                                            className={`w-8 h-8 rounded border flex items-center justify-center ${
                                                r.active !== false
                                                    ? "border-red-500 bg-red-900/20 hover:bg-red-900/50 text-red-300"
                                                    : "border-green-500 bg-green-900/20 hover:bg-green-900/50 text-green-300"
                                            }`}>
                                            {r.active !== false ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* billing info view modal */}
            {billingFor && (
                <BillingInfoView r={billingFor} onClose={() => setBillingFor(null)} />
            )}

            {/* gift modal */}
            {giftFor && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setGiftFor(null)}>
                    <div className="bg-surface border-2 border-purple-500 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sa-gift-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <Sparkles className="w-7 h-7 text-purple-300" />
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-purple-300">Regalo per</div>
                                <div className="font-display text-xl font-bold">{giftFor.name}</div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs uppercase tracking-wider text-elegant mb-2 block flex items-center gap-1">
                                    <CalIcon className="w-3 h-3" /> Giorni di trial da aggiungere
                                </label>
                                <input type="number" min="0" max="365" value={giftDays} onChange={(e) => setGiftDays(e.target.value)}
                                    data-testid="sa-gift-days"
                                    className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md text-warm focus:border-purple-500 focus:outline-none text-lg font-bold" />
                                <div className="flex gap-1 mt-1">
                                    {[7, 14, 30, 60, 90].map((d) => (
                                        <button key={d} onClick={() => setGiftDays(d)}
                                            data-testid={`sa-quick-days-${d}`}
                                            className="text-[10px] px-2 py-1 border border-[#1f2c44] rounded text-elegant hover:border-purple-500 hover:text-purple-300">
                                            +{d}gg
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-elegant mb-2 block flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" /> Crediti AI Vision da aggiungere
                                </label>
                                <input type="number" min="0" max="10000" value={giftAi} onChange={(e) => setGiftAi(e.target.value)}
                                    data-testid="sa-gift-ai"
                                    className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md text-warm focus:border-purple-500 focus:outline-none text-lg font-bold" />
                                <div className="flex gap-1 mt-1">
                                    {[10, 50, 100, 500].map((c) => (
                                        <button key={c} onClick={() => setGiftAi(c)}
                                            data-testid={`sa-quick-ai-${c}`}
                                            className="text-[10px] px-2 py-1 border border-[#1f2c44] rounded text-elegant hover:border-purple-500 hover:text-purple-300">
                                            +{c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={() => setGiftFor(null)} data-testid="sa-gift-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                            <button onClick={gift} data-testid="sa-gift-confirm"
                                disabled={(parseInt(giftDays, 10) || 0) <= 0 && (parseInt(giftAi, 10) || 0) <= 0}
                                className="flex-1 h-12 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900/40 disabled:cursor-not-allowed text-white font-bold rounded uppercase tracking-wider text-xs">
                                Conferma regalo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* block/unblock confirm */}
            {actionFor && (
                <BlockConfirm r={actionFor} onCancel={() => setActionFor(null)} onConfirm={(reason) => blockToggle(actionFor, reason)} />
            )}

            {/* premium grant/revoke modal */}
            {premiumFor && (
                <PremiumModal
                    r={premiumFor}
                    onCancel={() => setPremiumFor(null)}
                    onGrant={grantPremium}
                    onRevoke={revokePremium}
                />
            )}

            {/* change password */}
            {showPwd && (
                <ChangeCredentials onClose={() => setShowPwd(false)} token={token} />
            )}

            {showPricing && (
                <PricingModal onClose={() => setShowPricing(false)} token={token} />
            )}
        </div>
    );
};

// ---------- BILLING INFO VIEW (read-only per SA) ----------
const BillingInfoView = ({ r, onClose }) => {
    const bi = r.billing_info || {};
    const complete = r.billing_complete;
    const Row = ({ label, value, mono, copyable }) => (
        <div className="flex items-start justify-between gap-3 py-2 border-b border-[#1f2c44]/40">
            <div className="text-[10px] uppercase tracking-wider text-elegant pt-0.5 min-w-[140px]">{label}</div>
            <div className={`flex-1 text-right text-warm ${mono ? "font-mono text-sm" : ""}`}>
                {value || <span className="text-elegant italic">— non inserito</span>}
                {value && copyable && (
                    <button
                        onClick={() => { navigator.clipboard.writeText(value); }}
                        className="ml-2 text-[10px] text-brand hover:underline"
                        title="Copia"
                    >
                        copia
                    </button>
                )}
            </div>
        </div>
    );
    const updated = bi.updated_at ? new Date(bi.updated_at).toLocaleString("it-IT") : null;
    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-surface border-2 border-brand rounded-lg p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sa-billing-modal">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                        <FileText className="w-7 h-7 text-brand" />
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-brand">Dati di fatturazione</div>
                            <div className="font-display text-xl font-bold">{r.name}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                </div>

                {!complete && (
                    <div className="bg-amber-900/20 border border-amber-600/40 rounded-md p-3 mb-4 text-sm text-amber-200 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Il tenant non ha ancora completato i dati di fatturazione. Non potrà attivare l'abbonamento finché non li inserisce.
                    </div>
                )}
                {complete && (
                    <div className="bg-green-900/20 border border-green-600/40 rounded-md p-3 mb-4 text-sm text-green-200 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> Dati completi — il tenant può procedere al checkout.
                    </div>
                )}

                <div className="space-y-0">
                    <Row label="Ragione sociale / Nome" value={bi.ragione_sociale} copyable />
                    <Row label="P.IVA / Codice Fiscale" value={bi.vat_or_cf} mono copyable />
                    <Row label="Indirizzo" value={bi.address} copyable />
                    <Row label="Email fatturazione" value={bi.email} copyable />
                    <Row label="PEC / Codice SDI" value={bi.pec_or_sdi} mono copyable />
                    <Row label="Telefono" value={bi.phone} copyable />
                </div>

                {updated && (
                    <div className="text-[11px] text-elegant mt-4 text-right">
                        Ultimo aggiornamento: {updated}
                    </div>
                )}

                <div className="flex justify-end mt-5">
                    <button onClick={onClose} data-testid="sa-billing-close"
                        className="h-11 px-5 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded text-xs uppercase tracking-wider">
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};


// ---------- PRICING MODAL ----------
const PricingModal = ({ onClose, token }) => {
    const [pricing, setPricing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({});

    useEffect(() => {
        api.get("/superadmin/pricing", { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => {
                setPricing(data);
                setForm({
                    subscription_price_eur: data.subscription_price_eur,
                    ai_pack_price_eur: data.ai_pack_price_eur,
                    ai_pack_requests: data.ai_pack_requests,
                    free_ai_per_month: data.free_ai_per_month,
                    trial_discount_pct: data.trial_discount_pct,
                });
            })
            .catch(() => toast.error("Errore caricamento prezzi"))
            .finally(() => setLoading(false));
    }, [token]);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/superadmin/pricing", {
                subscription_price_eur: parseFloat(form.subscription_price_eur),
                ai_pack_price_eur: parseFloat(form.ai_pack_price_eur),
                ai_pack_requests: parseInt(form.ai_pack_requests, 10),
                free_ai_per_month: parseInt(form.free_ai_per_month, 10),
                trial_discount_pct: parseFloat(form.trial_discount_pct),
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Prezzi aggiornati. I tenant vedranno i nuovi valori subito.");
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore salvataggio");
        }
        setBusy(false);
    };

    const resetDefaults = () => {
        if (!pricing) return;
        setForm({
            subscription_price_eur: pricing.defaults.subscription_price_eur,
            ai_pack_price_eur: pricing.defaults.ai_pack_price_eur,
            ai_pack_requests: pricing.defaults.ai_pack_requests,
            free_ai_per_month: pricing.defaults.free_ai_per_month,
            trial_discount_pct: pricing.defaults.trial_discount_pct,
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4">
                <div className="text-elegant">Caricamento prezzi...</div>
            </div>
        );
    }

    const subPrice = parseFloat(form.subscription_price_eur) || 0;
    const discountPct = parseFloat(form.trial_discount_pct) || 0;
    const trialPrice = subPrice * (1 - discountPct / 100);

    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-surface border-2 border-emerald-500 rounded-lg p-6 max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sa-pricing-modal">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Euro className="w-7 h-7 text-emerald-300" />
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Listino app</div>
                            <div className="font-display text-xl font-bold">Prezzi di vendita</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-red-300"><X className="w-5 h-5" /></button>
                </div>

                {pricing.updated_at && (
                    <div className="text-[10px] text-elegant mb-3">
                        Ultima modifica: {new Date(pricing.updated_at).toLocaleString("it-IT")} da <strong>{pricing.updated_by}</strong>
                    </div>
                )}

                <div className="space-y-4">
                    {/* Abbonamento */}
                    <div className="bg-deep border border-[#1f2c44] rounded p-3">
                        <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-2 flex items-center gap-1">
                            <Crown className="w-3 h-3" /> Abbonamento mensile Premium
                        </div>
                        <div className="flex items-center bg-[#0a1322] border border-[#1f2c44] rounded">
                            <span className="px-3 text-elegant">€</span>
                            <input type="number" step="0.01" min="0" max="10000"
                                value={form.subscription_price_eur ?? ""}
                                onChange={(e) => setForm({ ...form, subscription_price_eur: e.target.value })}
                                data-testid="sa-pricing-sub"
                                className="flex-1 h-12 bg-transparent text-warm text-xl font-bold font-mono focus:outline-none" />
                            <span className="px-3 text-elegant text-xs">/mese</span>
                        </div>
                    </div>

                    {/* Sconto trial */}
                    <div className="bg-deep border border-[#1f2c44] rounded p-3">
                        <div className="text-xs uppercase tracking-wider text-elegant font-bold mb-2">
                            Sconto trial (primo mese)
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center bg-[#0a1322] border border-[#1f2c44] rounded flex-1">
                                <input type="number" step="1" min="0" max="100"
                                    value={form.trial_discount_pct ?? ""}
                                    onChange={(e) => setForm({ ...form, trial_discount_pct: e.target.value })}
                                    data-testid="sa-pricing-discount"
                                    className="flex-1 h-11 px-3 bg-transparent text-warm font-mono focus:outline-none" />
                                <span className="px-3 text-elegant text-xs">%</span>
                            </div>
                            <div className="text-xs text-emerald-300">
                                = € {trialPrice.toFixed(2)} <span className="opacity-60">il primo mese</span>
                            </div>
                        </div>
                    </div>

                    {/* AI Pack */}
                    <div className="bg-deep border border-[#1f2c44] rounded p-3">
                        <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-2 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Pacchetto AI aggiuntivo
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">Prezzo (€)</div>
                                <div className="flex items-center bg-[#0a1322] border border-[#1f2c44] rounded">
                                    <span className="px-3 text-elegant">€</span>
                                    <input type="number" step="0.01" min="0" max="1000"
                                        value={form.ai_pack_price_eur ?? ""}
                                        onChange={(e) => setForm({ ...form, ai_pack_price_eur: e.target.value })}
                                        data-testid="sa-pricing-ai-price"
                                        className="flex-1 h-11 bg-transparent text-warm font-mono focus:outline-none" />
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">N. richieste</div>
                                <div className="flex items-center bg-[#0a1322] border border-[#1f2c44] rounded">
                                    <input type="number" step="1" min="1" max="10000"
                                        value={form.ai_pack_requests ?? ""}
                                        onChange={(e) => setForm({ ...form, ai_pack_requests: e.target.value })}
                                        data-testid="sa-pricing-ai-qty"
                                        className="flex-1 h-11 px-3 bg-transparent text-warm font-mono focus:outline-none" />
                                </div>
                            </div>
                        </div>
                        {parseFloat(form.ai_pack_price_eur) > 0 && parseInt(form.ai_pack_requests, 10) > 0 && (
                            <div className="text-[10px] text-elegant mt-2">
                                ≈ € {(parseFloat(form.ai_pack_price_eur) / parseInt(form.ai_pack_requests, 10)).toFixed(3)} per richiesta
                            </div>
                        )}
                    </div>

                    {/* Free AI */}
                    <div className="bg-deep border border-[#1f2c44] rounded p-3">
                        <div className="text-xs uppercase tracking-wider text-elegant font-bold mb-2">
                            Richieste AI gratuite incluse / mese
                        </div>
                        <div className="flex items-center bg-[#0a1322] border border-[#1f2c44] rounded">
                            <input type="number" step="1" min="0" max="1000"
                                value={form.free_ai_per_month ?? ""}
                                onChange={(e) => setForm({ ...form, free_ai_per_month: e.target.value })}
                                data-testid="sa-pricing-free-ai"
                                className="flex-1 h-11 px-3 bg-transparent text-warm font-mono focus:outline-none" />
                            <span className="px-3 text-elegant text-xs">/mese gratis</span>
                        </div>
                    </div>
                </div>

                <div className="bg-amber-900/20 border border-amber-500/40 rounded p-2 mt-4 text-[10px] text-amber-200">
                    ⚠ Le modifiche entrano in vigore immediatamente per TUTTI i tenant. Gli acquisti già completati restano invariati.
                </div>

                <div className="flex gap-2 mt-5">
                    <button onClick={resetDefaults} data-testid="sa-pricing-reset"
                        className="h-12 px-4 border border-[#1f2c44] text-elegant hover:border-brand rounded text-xs uppercase tracking-wider">
                        Default
                    </button>
                    <button onClick={onClose} disabled={busy}
                        className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                    <button onClick={save} disabled={busy} data-testid="sa-pricing-save"
                        className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded uppercase tracking-wider text-xs disabled:opacity-60">
                        {busy ? "Salvataggio..." : "Salva prezzi"}
                    </button>
                </div>
            </div>
        </div>
    );
};


// ---------- PREMIUM GRANT / REVOKE MODAL ----------
const PremiumModal = ({ r, onCancel, onGrant, onRevoke }) => {
    const isActive = !!r.premium_complimentary;
    const expIso = r.premium_expires_at;
    const expDate = expIso ? new Date(expIso) : null;
    const perpetual = !!r.premium_perpetual;

    const DURATION_PRESETS = [
        { months: 1, label: "1 mese" },
        { months: 3, label: "3 mesi" },
        { months: 6, label: "6 mesi" },
        { months: 12, label: "12 mesi" },
    ];

    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div className="bg-surface border-2 border-purple-500 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sa-premium-modal">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-700/30 border border-purple-400 flex items-center justify-center">
                        <Crown className="w-6 h-6 text-purple-200" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-purple-300">Premium omaggio</div>
                        <div className="font-display text-xl font-bold">{r.name}</div>
                    </div>
                </div>

                {/* stato corrente */}
                {isActive ? (
                    <div className="bg-purple-900/30 border border-purple-500/40 rounded-md p-3 mb-5 text-sm" data-testid="sa-premium-status-active">
                        <div className="flex items-center gap-2 font-bold text-purple-100 mb-1">
                            <Crown className="w-4 h-4" /> Premium attivo (omaggio)
                        </div>
                        <div className="text-xs text-purple-200">
                            {perpetual
                                ? <span className="flex items-center gap-1"><InfinityIcon className="w-3 h-3" /> Senza scadenza (a vita)</span>
                                : (expDate ? `Scade il ${expDate.toLocaleDateString("it-IT")} (${Math.max(0, Math.ceil((expDate - new Date()) / 86400000))} gg)` : "Scadenza non impostata")
                            }
                        </div>
                    </div>
                ) : (
                    <div className="bg-deep border border-[#1f2c44] rounded-md p-3 mb-5 text-sm text-elegant" data-testid="sa-premium-status-inactive">
                        Questo ristorante <strong>NON</strong> ha Premium omaggio attivo. Scegli una durata per attivarlo.
                    </div>
                )}

                {/* attiva / cambia */}
                <div className="space-y-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">
                        {isActive ? "Estendi / Cambia durata" : "Attiva Premium gratuito"}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {DURATION_PRESETS.map((d) => (
                            <button
                                key={d.months}
                                onClick={() => onGrant(d.months, false)}
                                data-testid={`sa-premium-grant-${d.months}m`}
                                className="h-12 border border-purple-500/50 bg-purple-900/20 hover:bg-purple-900/50 text-purple-100 rounded font-bold text-sm uppercase tracking-wider"
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => onGrant(null, true)}
                        data-testid="sa-premium-grant-perpetual"
                        className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white rounded font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                    >
                        <InfinityIcon className="w-4 h-4" /> Perpetuo (a vita)
                    </button>
                </div>

                {/* revoca */}
                {isActive && (
                    <div className="mt-5 pt-5 border-t border-[#1f2c44]">
                        <button
                            onClick={() => {
                                if (window.confirm(`Revocare il Premium omaggio per ${r.name}?`)) onRevoke();
                            }}
                            data-testid="sa-premium-revoke"
                            className="w-full h-11 border border-red-500/50 bg-red-900/20 hover:bg-red-900/50 text-red-200 rounded font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                        >
                            <Ban className="w-4 h-4" /> Disattiva Premium
                        </button>
                        <div className="text-[10px] text-elegant mt-1 text-center">
                            Se il trial originale è ancora valido, il tenant resterà in trial residuo; altrimenti l'abbonamento scade.
                        </div>
                    </div>
                )}

                <div className="flex justify-end mt-5">
                    <button onClick={onCancel} data-testid="sa-premium-close"
                        className="h-11 px-5 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded text-xs uppercase tracking-wider">
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};


// ---------- BLOCK CONFIRM ----------
const BlockConfirm = ({ r, onCancel, onConfirm }) => {
    const [reason, setReason] = useState("");
    const blocking = r.active !== false;
    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div className="bg-surface border-2 border-red-500 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sa-block-modal">
                <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-7 h-7 text-red-400" />
                    <div className="font-display text-xl font-bold text-red-200">
                        {blocking ? "Blocca" : "Sblocca"} {r.name}?
                    </div>
                </div>
                <p className="text-elegant text-sm mb-4">
                    {blocking
                        ? "Gli utenti non potranno più accedere finché non sblocchi. Gli ordini in corso restano nel database."
                        : "Gli utenti torneranno a poter accedere normalmente."}
                </p>
                {blocking && (
                    <div className="mb-4">
                        <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Motivo (opzionale)</label>
                        <input value={reason} onChange={(e) => setReason(e.target.value)}
                            data-testid="sa-block-reason"
                            placeholder="Es. mancato pagamento, abuso, su richiesta..."
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-red-500 focus:outline-none text-sm" />
                    </div>
                )}
                <div className="flex gap-2">
                    <button onClick={onCancel} data-testid="sa-block-cancel"
                        className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                    <button onClick={() => onConfirm(reason)} data-testid="sa-block-confirm"
                        className={`flex-1 h-12 font-bold rounded uppercase tracking-wider text-xs text-white ${
                            blocking ? "bg-red-600 hover:bg-red-500" : "bg-green-600 hover:bg-green-500"
                        }`}>
                        {blocking ? "Blocca" : "Sblocca"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- CHANGE PASSWORD ----------
const ChangeCredentials = ({ onClose, token }) => {
    const [cur, setCur] = useState("");
    const [nu, setNu] = useState("");
    const [np1, setNp1] = useState("");
    const [np2, setNp2] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (np1 && np1 !== np2) return toast.error("Le nuove password non coincidono");
        if (np1 && np1.length < 6) return toast.error("Min 6 caratteri");
        setBusy(true);
        try {
            await api.patch("/superadmin/credentials", {
                current_password: cur,
                new_username: nu || null,
                new_password: np1 || null,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Credenziali aggiornate");
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-surface border-2 border-brand rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sa-pwd-modal">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-xl font-bold flex items-center gap-2">
                        <KeyRound className="w-5 h-5 text-brand" /> Cambia credenziali
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Password attuale</label>
                        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                            data-testid="sa-pwd-current"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Nuovo username (opzionale)</label>
                        <input value={nu} onChange={(e) => setNu(e.target.value)} placeholder="lascia vuoto per non cambiare"
                            data-testid="sa-pwd-username"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Nuova password (opzionale)</label>
                        <input type="password" value={np1} onChange={(e) => setNp1(e.target.value)}
                            data-testid="sa-pwd-new"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Conferma nuova password</label>
                        <input type="password" value={np2} onChange={(e) => setNp2(e.target.value)}
                            data-testid="sa-pwd-confirm"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none" />
                    </div>
                </div>
                <div className="flex gap-2 mt-5">
                    <button onClick={onClose} data-testid="sa-pwd-cancel"
                        className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                    <button onClick={submit} disabled={busy} data-testid="sa-pwd-save"
                        className="flex-1 h-12 bg-brand text-deep font-bold rounded uppercase tracking-wider text-xs disabled:opacity-50">
                        {busy ? "Salvataggio..." : "Salva"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SuperAdmin;
